import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import "../css/BoardOfDirectors.css"; // Import CSS file

// Define Director Type
interface Director {
  id: number;
  name: string;
  title: string;
  image: string;
  bio: string;
}

// Sample data for directors
const directors: Director[] = [
   {
    id: 1,
    name: "SOK IM",
    title: "President",
    image: "assets/images/madam.jpg",
    bio: "Madam Sok Im has demonstrated exceptional dedication to enhancing Cambodia's business landscape.\n\nSince the late 1990s, she has successfully ventured into diverse industries, including trading, real estate investment, and, most recently, Information and Communications Technology (ICT).",
  },
  // {
  //   id: 2,
  //   name: "SIENG SODANIN",
  //   title: "Deputy Group CEO",
  //   image: "assets/images/mrs_sodanin.jpg",
  //   bio: "Sodanin holds a Bachelor’s Degree in Public Health from Johns Hopkins University and a Master’s Degree in Public Administration from Cornell University in the United States.\n\nUpon her return to Cambodia in 2014, she contributed her expertise by serving at the Ministry of Economy and Finance.",
  // },
  //  {
  //   id: 2,
  //   name: "KHUT PISEY",
  //   title: "Financial Controller",
  //   image: "assets/images/mrss_pisey.jpg",
  //   bio: "Pisey is a results-driven Financial Controller with extensive experience in financial management, compliance, and strategic planning across diverse industries including data centers, telecommunications, and property development. \n\nWith a strong foundation in strategic finance, treasury management, and risk control, Pisey plays a vital role in aligning financial integrity with corporate growth objectives. Dedicated to excellence and continuous improvement, she combines strategic foresight with a hands-on leadership approach to build resilient finance teams and drive sustainable business performance.",
  // },
  {
    id: 2,
    name: "META SY",
    title: "Advisory Board Member",
    image: "assets/images/mr_meta.jpg",
    bio: "Meta is the Group CEO of Sayon and a member of its Board of Directors. Appointed as Group CEO in 2021, Meta previously served as the CEO of SINET, where he played a pivotal role in establishing SINET’s telecom infrastructure from the ground up and transforming it into one of Cambodia’s leading operators.\n\nHe also oversees the business development, design, construction, and operations of Chaktomuk Data Center.\n\nBefore joining SINET, Meta gained extensive experience working for an Internet Service Provider in Madagascar for nearly six years.  Earlier in his career, he served as a System and Network Administrator at Telstra BigPond.",
  },
  {
    id: 3,
    name: "SENG SOTHY",
    title: "Advisory Board Member",
    image: "assets/images/mr_sothy.jpg",
    bio: "Sothy serves as the General Manager of Sayon and is a valued member of its Board of Directors. He oversees property development, design, and construction projects, while also managing all property management services for Sayon and its subsidiary companies.\n\nSince joining SI Group in 2005, following nearly two decades of service at the Ministry of Tourism, Sothy has leveraged his extensive knowledge of Cambodia’s geography to spearhead the construction of office buildings across the country.\n\nThese developments play a critical role in supporting SINET’s network infrastructure through the strategic utilization of these properties.",
  },
  {
    id: 4,
    name: "Wifrid Dutruel",
    title: "Advisory Board Member",
    image: "assets/images/wifrid_dutruel.png",
    bio: "Wilfrid Dutruel is a highly strategic and results-oriented leader, currently serving as Chief Operating Officer (COO) at Chaktomuk Data Center, Cambodia's pioneering and only Tier III Plus Design Center. In this pivotal role, he spearheads the provision of robust co-location and managed IT infrastructure services, driving growth in cloud services and cybersecurity within the region's rapidly evolving technology landscape. His leadership is instrumental in shaping the operational excellence and strategic direction of this critical infrastructure. \n\n Concurrently, Wilfrid is the managing director of MEP-E, where he leads a premier engineering firm specializing in cost-effective and responsive Mechanical, Electrical, and Plumbing (MEP) solutions. Under his guidance, MEP-E delivers high-quality designs for diverse projects, from residential to large-scale commercial and high-rise developments, adhering to stringent international standards in HVAC systems, electrical solutions, and sustainable plumbing designs."
  },
  {
    id: 5,
    name: "KHUT PISEY",
    title: "Financial Controller",
    image: "assets/images/mrss_pisey.jpg",
    bio: "Pisey is a results-driven Financial Controller with extensive experience in financial management, compliance, and strategic planning across diverse industries including data centers, telecommunications, and property development. \n\nWith a strong foundation in strategic finance, treasury management, and risk control, Pisey plays a vital role in aligning financial integrity with corporate growth objectives. Dedicated to excellence and continuous improvement, she combines strategic foresight with a hands-on leadership approach to build resilient finance teams and drive sustainable business performance.",
  },
];

// Framer Motion Variants
const containerVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { staggerChildren: 0.15, duration: 0.6 }, // Adds stagger effect
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const DirectorCard: React.FC<{
  director: Director;
  isExpanded: boolean;
  onToggle: () => void;
}> = ({ director, isExpanded, onToggle }) => (
  <motion.div
    className={`card ${isExpanded ? "expanded-card" : ""}`}
    key={director.id}
    variants={cardVariants}
    whileHover={{ scale: 1.06 }}
    transition={{ duration: 0.1, ease: "easeInOut" }}
  >
    <div className="image-container">
      <img src={director.image} alt={director.name} />
    </div>
    <h4>{director.name}</h4>
    <h5>{director.title}</h5>

    {/* Expandable Section */}
    <AnimatePresence>
      {isExpanded && (
        <motion.div
          className="expandable"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2, ease: "easeInOut" }}
        >
          <div className="bio-container">
            <p className="bio">{director.bio}</p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>

    {/* Expand Button */}
    <motion.button onClick={onToggle}>
      {isExpanded ? "Less information" : "More information"}
      <ChevronDown
        size={16}
        className={`chevron ${isExpanded ? "rotate" : ""}`}
      />
    </motion.button>
  </motion.div>
);

const BoardOfDirectors: React.FC = () => {
  const [expanded, setExpanded] = useState<number | null>(null);

  // Toggle expanded card state
  const toggleExpand = (id: number) => {
    setExpanded(expanded === id ? null : id);
  };

  return (
    <motion.section
      className="board-section"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: false, amount: 0.1 }}
      variants={containerVariants}
    >
      <motion.div
        className="board-header"
        initial={{ opacity: 0, y: -40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        viewport={{ once: false }}
      >
        <h3>Meet Us</h3>
        <h2>Board of Directors</h2>
        <p>
          Our Board of Directors lies at the core of Sok Im Holding's success.
          Comprising highly qualified and experienced professionals, they bring
          exceptional expertise and leadership to drive the company’s vision
          forward. Their dedication to excellence and commitment to upholding
          the highest standards guide Sok Im Holding in delivering outstanding
          value across its diverse business sectors.
        </p>
      </motion.div>

      <motion.div className="directors-container" variants={containerVariants}>
        <div className="directors-row">
          {directors.slice(0, 1).map((director) => (
            <DirectorCard
              key={director.id}
              director={director}
              isExpanded={expanded === director.id}
              onToggle={() => toggleExpand(director.id)}
            />
          ))}
        </div>
        <div className="directors-row">
          {directors.slice(1, 5).map((director) => (
            <DirectorCard
              key={director.id}
              director={director}
              isExpanded={expanded === director.id}
              onToggle={() => toggleExpand(director.id)}
            />
          ))}
        </div>
      </motion.div>
    </motion.section>
  );
};

export default BoardOfDirectors;
